import { FC, useMemo } from 'react'
import { HOURS } from '@sequencehq/utils/dates'
import {
  Text,
  IconUserDuo,
  Badge,
  Button,
  Skeleton,
  SequenceMenu,
  SetIsOpen,
  RedRed20,
  RedRed50,
  MagicTable,
  MagicTableCell,
  MagicTableCellEmpty,
  useMagicTableInfiniteQuery,
  usePersistMagicTableQueryParams,
  MagicTableFilterConfig,
  useLinkMagicTableWithSearchParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  IconCalendar,
  IconCode,
  IconConsole,
  IconHashtag,
  IconPlus,
  Flex
} from '@sequencehq/core-components'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  SeatSnapshotsPropFilters,
  SeatSnapshotsView
} from 'modules/Seats/SeatBalances/types'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import {
  customerIdFilterConfig,
  LinkToCustomerMenuItem,
  LinkToNewCustomerMenuItem
} from 'Customer/index'
import { timestampFilterOptions } from 'UsageEvents/index'
import { isoTimestampIgnoringTime } from '@sequencehq/utils/dates'
import { useSeatMetrics } from 'Seats/SeatMetric/useSeatMetrics'
import Spinner from 'components/Loading'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@sequencehq/api'
import { SeatTypeBadge } from 'Seats/common/SeatTypeBadge'
import { CreateSeatSnapshotDrawer } from './CreateSnapshot/CreateSeatSnapshotDrawer'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { useLogLoadTime } from 'lib/postHog/useLogLoadTime'

export const SeatSnapshotsMagicTable = () => {
  const navigate = useNavigate()

  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()

  const { metrics: seatMetrics, loading: loadingSeatMetrics } = useSeatMetrics()

  const aliasesInfiniteQuery = useLoadAllItemsQuery(
    dashboard20240730Client.getCustomerAliases,
    { limit: 100 },
    { staleTime: 1 * HOURS }
  )

  const aliases = useMemo(
    () => aliasesInfiniteQuery.data?.pages.flatMap(page => page.items),
    [aliasesInfiniteQuery.data]
  )

  const filters: MagicTableFilterConfig<SeatSnapshotsPropFilters>[] = [
    {
      type: 'text',
      paramName: 'customerEventId',
      label: 'Event ID',
      icon: IconCode
    },
    {
      type: 'multiSelect',
      paramName: 'seatType',
      options: (seatMetrics ?? []).map(seatMetric => ({
        label: seatMetric.label,
        value: seatMetric.seatType
      })),
      format: value => <Text>{value}</Text>,
      label: 'Seat metric',
      icon: IconConsole
    },
    {
      ...customerIdFilterConfig,
      paramName: 'customerIds'
    },
    {
      type: 'multiSelect',
      paramName: 'customerAlias',
      options: (aliases ?? []).map(({ value }) => ({
        label: value,
        value
      })),
      format: value => {
        const alias = (aliases ?? []).find(
          ({ value: customerAlias }) => customerAlias === value
        )
        const aliasValue = alias?.value ?? '-'
        return <Badge>{aliasValue}</Badge>
      },
      label: 'Customer alias',
      icon: IconHashtag
    },
    {
      type: 'date',
      paramName: 'eventTimestamp',
      paramNameBefore: 'eventTimestampBefore',
      paramNameAfter: 'eventTimestampAfter',
      optionsBefore: timestampFilterOptions,
      optionsAfter: timestampFilterOptions,
      format: (_, label) => label,
      label: 'Timestamp',
      stringifyDate: (date: Date) => {
        return isoTimestampIgnoringTime(date, 'start')
      },
      stringifyDateBefore: (date: Date) =>
        isoTimestampIgnoringTime(date, 'end'),
      stringifyDateAfter: (date: Date) =>
        isoTimestampIgnoringTime(date, 'start'),
      icon: IconCalendar
    }
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filters)

  const { can } = usePermissions()
  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getSeatEvents,
    filters,
    activeFilters,
    sortBy
  )

  useLogLoadTime({
    eventName: 'seat_events_loaded',
    ready: !loadingSeatMetrics && infiniteQuery.isFetched
  })

  if (loadingSeatMetrics) {
    return <Spinner></Spinner>
  }

  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<SeatSnapshotsView>
          activeFilters={activeFilters}
          onChangeActiveFilters={onChangeActiveFilters}
          sortBy={sortBy}
          onChangeSortBy={onChangeSortBy}
          entityNamePlural="seat events"
          sequenceUserId={userId}
          infiniteQuery={infiniteQuery}
          enableCopyResults
          disableActiveRowScroll
          rowPath={(row: SeatSnapshotsView['model']) =>
            persistMagicTableQueryParams(`/seat-events/${row.id}`)
          }
          columns={[
            {
              id: 'total',
              accessorKey: 'total',
              header: 'Total Seats',
              cell: value => {
                const total =
                  value.getValue<SeatSnapshotsView['model']['total']>()
                return <MagicTableCell text={`${total ?? 0}`} />
              }
            },
            {
              id: 'customer',
              accessorKey: 'customerAlias',
              header: 'Customer',
              cell: value => {
                const customerAliasQuery = useQuery(
                  dashboard20240730Client.getCustomerByAlias,
                  {
                    alias:
                      value.getValue<
                        SeatSnapshotsView['model']['customerAlias']
                      >()
                  },
                  {
                    retry: false
                  }
                )

                if (customerAliasQuery.isPending) {
                  return (
                    <Flex height="40px" alignItems="center">
                      <Skeleton height="24px" width="100%" />
                    </Flex>
                  )
                }

                if (!customerAliasQuery.data) {
                  return (
                    <MagicTableCell
                      text="Alias not mapped to Customer"
                      borderColor={RedRed20}
                      textColor={RedRed50}
                    />
                  )
                }

                return (
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    height="40px"
                    pl="6px"
                  >
                    <CustomerPreviewCardPill
                      customerId={customerAliasQuery.data.id}
                      variant="ghost"
                    />
                  </Flex>
                )
              }
            },
            {
              id: 'seatType',
              accessorKey: 'seatType',
              header: 'Seat type',
              cell: value => (
                <MagicTableCellEmpty>
                  <SeatTypeBadge
                    seatType={value.getValue<
                      SeatSnapshotsView['model']['seatType']
                    >()}
                  />
                </MagicTableCellEmpty>
              ),
              enableSorting: true
            },
            {
              id: 'customerEventId',
              accessorKey: 'customerEventId',
              header: 'Event ID',
              cell: value => (
                <MagicTableCell
                  text={value.getValue<
                    DashboardApi20240730.GetSeatEvents.SeatEvent['customerEventId']
                  >()}
                  showCopyButton={true}
                />
              )
            },
            {
              id: 'snapshotTimestamp',
              accessorKey: 'eventTimestamp',
              header: 'Timestamp (UTC)',
              cell: value => (
                <MagicTableCell
                  text={value.getValue<
                    DashboardApi20240730.GetSeatEvents.SeatEvent['eventTimestamp']
                  >()}
                />
              ),
              enableSorting: true
            }
          ]}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="seatEvents.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/gauge.png"
              learnMoreText="Leverage comprehensive seat-based billing infrastructure"
              learnMoreHref="https://docs.sequencehq.com/seats/seat-updates"
              buttonNew={
                can('org.usage:create') ? (
                  <CreateSeatSnapshotDrawer
                    trigger={
                      <Button
                        variant="primary"
                        leadingIcon={<IconPlus color="inherit" />}
                        data-testid="seatEvents.table.new-button"
                      >
                        New seat event
                      </Button>
                    }
                  />
                ) : (
                  <ProtectedButton permission="org.usage:create">
                    <Button
                      variant="primary"
                      leadingIcon={<IconPlus color="inherit" />}
                      data-testid="seatEvents.table.new-button"
                    >
                      New seat event
                    </Button>
                  </ProtectedButton>
                )
              }
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              entityIcon={IconUserDuo}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate('/seat-events')}
              variant="TABLE"
            />
          )}
          filters={filters}
          kebabMenu={(model: SeatSnapshotsView['model'], props) => (
            <SeatEventKebabMenu model={model} {...props} />
          )}
        />
      )}
    </CurrentUserId>
  )
}

const SeatEventKebabMenu: FC<{
  model: SeatSnapshotsView['model']
  [key: string]: unknown
}> = ({ model, ...props }) => {
  const customerAliasQuery = useQuery(
    dashboard20240730Client.getCustomerByAlias,
    { alias: model.customerAlias },
    { retry: false, enabled: !!model.customerAlias }
  )

  const isLinked = !!customerAliasQuery.data
  if (isLinked) return null

  return (
    <SequenceMenu
      data-testid={`seatEvents.${model.id}.menu`}
      buttonSize="small"
      alignment="bottom-right"
      items={[
        ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
          <LinkToCustomerMenuItem
            key="link-to-customer"
            testIdPrefix="seatEvents"
            eventId={model.id}
            alias={model.customerAlias}
            onClose={() => setIsOpen?.(false)}
            onSuccess={() => setIsOpen?.(false)}
          />
        ),
        ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
          <LinkToNewCustomerMenuItem
            key="link-to-new-customer"
            testIdPrefix="seatEvents"
            eventId={model.id}
            alias={model.customerAlias}
            onClose={() => setIsOpen?.(false)}
          />
        )
      ]}
      {...props}
    />
  )
}
